import { Component, Input, OnChanges, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { Chart, ChartType, ChartDataset} from 'chart.js';
import { NWMChartSettings } from './NWMChartSettings';
import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { tooltipType } from "app/components/NWMCharts/NWMCharts.module";
import { AppConfig } from "app/app.config";
import { BaseChartDirective } from 'ng2-charts';

type apiDataType = {
  Percentage: string;
  Value: number;
  Year: number;
}[];

@Component({
    selector: 'CFCYChart',
    templateUrl: './nwmchart.component.html',
    styleUrls: ['./nwmchart.component.scss'],
    imports: [BaseChartDirective]
})

export class CFCYChartComponent implements OnChanges {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private config = inject(AppConfig);
  private ref = inject(ChangeDetectorRef);

  @Input() plan?: string;
  @Input() limit?: number;
  @Output() loaded : EventEmitter<boolean> = new EventEmitter();

  public dataLoaded = false;
  public chartData: ChartDataset[] = [{data:[], backgroundColor: '#88A8A3'}];
  public chartLabels: string[] = [];
  public chartType: ChartType = 'bar';
  public chartLegend = false;
  public chartPercent?: string[];
  public tooltip: tooltipType = {visible:false, rows:[]};
  public chartSettings: NWMChartSettings;
  public width = 500;
  public height = 290;

  constructor() {
    this.chartSettings = new NWMChartSettings();
  }

  ngOnChanges(): void {
    this.dataLoaded = false;
    this.tooltip.visible = false;
    this.chartSettings.options.plugins = {tooltip:{enabled: false, external: (a)=>this.customTooltip(a.chart)}};

    this.chartSettings.options.layout={padding:{top:18}};
    this.chartSettings.options.animation = {
      duration: 0,
      onComplete : (context) => {
        const ctx = context.chart.ctx;

        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#333';
        const minF = this.chartSettings.getFontSize(context.chart.boxes[0].width);
        ctx.font = Math.max(12, minF).toString() + "px 'Neue Montreal', 'Geneva', 'sans-serif'";

        context.chart.getSortedVisibleDatasetMetas().forEach(metaset => {
          metaset.data.forEach((model, j)=>{
            ctx.fillText(this.chartPercent![j], model.x, 17);
          })
        });
      }
    }
    this.loadChart();
  }

  customTooltip(chart: Chart): void {
    const tooltipModel = chart.tooltip!;
    this.tooltip.visible = tooltipModel.opacity !== 0;
    if (tooltipModel.body) {
      const i = tooltipModel.dataPoints[0].dataIndex;
      this.tooltip.rows = [
        { key: 'Year:',    value: tooltipModel.title[0] },
        { key: 'Cash Flow:', value: this.api.formatCurrency(this.chartData[0].data[i] as number) },
        { key: 'Cash Yield:', value: this.chartPercent![i] }
      ];
    }
    setTimeout(()=>{this.ref.detectChanges();});
  }

  loadChart() {
    this.loaderService.display(true, 'CFCY');
    const reRg = this.auth.getCurrentReRg()!;
    const vd = this.auth.getCurrentValuationDate();
    const planPath = this.api.getPlanPath(this.plan || '', reRg.IsPershing);

    this.api.get<apiDataType>(`${this.config.portfolio.portfolioSummaryCashFlowAnnualUrl}/${reRg.ReRgId}/${vd}/${planPath}`).subscribe(
      data => {
        this.loaded.emit(false);
        this.dataLoaded = false;
        setTimeout(()=>{this.dataLoaded = true});
        this.chartData[0].data = [];
        if (!data || !data.length) return;
        if (this.limit) data = data.slice(-this.limit);
        this.chartLabels = [];
        const chartData: number[] = [];
        this.chartPercent = [];
        for (let i = 0; i < data.length; i++) {
          this.chartLabels.push(data[i].Year.toString());
          chartData.push(+data[i].Value);
          this.chartPercent.push( parseFloat(data[i].Percentage).toFixed(1) + '%' );
        }

        this.chartData[0].data = chartData;
        this.chartSettings.options.scales!['y']!.suggestedMax = this.chartSettings.getSuggestedMax(this.chartData[0].data as number[], .2);
        this.chartSettings.options.scales!['y']!.ticks!.callback = (value, index, ticks) =>
          this.chartSettings.getYAxisLabelToShow(ticks.map(t=>t.value), value as number);
        this.loaded.emit(true);
        this.loaderService.display(false, 'CFCY');
      },
      () => {
        this.loaded.emit(false);
        this.loaderService.display(false, 'CFCY');
      }
    );
  }
}
